import { Box, Stack } from '@mui/material';
import { styled, SxProps } from '@mui/material/styles';

import { getThemeColor, TMoplaColorsV2 } from '@mopla/mui';

const excludedProps = ['color', 'left', 'size'];

export const StyledTicketContainer = styled(Box)({
	width: '100%',
	boxShadow:
		'-1px 13px 6px -4px rgba(24, 39, 75, 0.08), 0 3px 16px 4px rgba(24, 39, 75, 0.08)',
	borderRadius: 16,
	overflow: 'hidden',
});

export const StyledCardContainer = styled(Box)(({ theme }) => ({
	boxSizing: 'border-box',
	backgroundColor: theme.palette.neutral[0],
	paddingTop: '14px',
	paddingLeft: '16px',
	paddingRight: '16px',
	paddingBottom: '16px',
	width: '100%',
	borderRadius: 16,
	position: 'relative',
	border: `0.5px solid ${theme.palette.primary[200]}`,
}));

export const StyledHeaderContainer = styled(Stack)({
	flexDirection: 'row',
	alignItems: 'flex-end',
	justifyContent: 'space-between',
	gap: '16px',
	height: '54px',
	marginBottom: '6px',
});

export const StyledHeaderSectionWrapper = styled(Stack)({
	flexDirection: 'column',
	alignItems: 'flex-end',
	gap: '4px',
});

export const StyledAnimatedMoplaTicketingLogoWrapper = styled(Box)({
	width: '98px',
	height: '54px',
	display: 'flex',
	alignItems: 'flex-end',
	justifyContent: 'center',

	'& svg': {
		width: '100%',
		height: '100%',
		display: 'block',
	},
});

export const StyledTicketSeparator = styled('div', {
	shouldForwardProp: (prop) => !excludedProps.includes(prop as string),
})<{ color?: TMoplaColorsV2; size?: number }>(
	({ theme, color, size = 17 }) => ({
		background: color ? getThemeColor(color, theme) : theme.palette.neutral[0],
		display: 'grid',
		gridTemplateColumns: 'max-content 1fr max-content',
		height: size,
		width: 'calc(100% + 32px)',
		margin: '0 -16px',
	})
);

export const DottedLine = styled('div', {
	shouldForwardProp: (prop) => !excludedProps.includes(prop as string),
})<{ color?: TMoplaColorsV2 }>(({ theme, color }) => ({
	height: 0,
	width: '100%',
	border: `0.5px dashed ${
		color ? getThemeColor(color, theme) : theme.palette.neutral[0]
	}`,
	alignSelf: 'center',
}));

export const SideCircle = styled('div', {
	shouldForwardProp: (prop) => !excludedProps.includes(prop as string),
})<{
	left?: boolean;
	backgroundColor?: string;
	size?: number;
}>(({ theme, left, backgroundColor = '#f2f3f4ff', size = 17 }) => ({
	background: backgroundColor,
	height: size,
	width: size,
	borderRadius: '50%',
	margin: left ? `0 0 0 -${size / 2}px` : `0 -${size / 2}px 0 0`,
	border: `0.5px solid ${theme.palette.primary[200]}`,
	boxSizing: 'border-box',
	position: 'relative',
	zIndex: 1,
}));

export const StyledBottomContainer = styled(Stack)({
	flexDirection: 'column',
	gap: '16px',
	paddingTop: '8px',
	marginTop: '8px',
});

export const StyledTicketDetailsSection = styled(Stack)({
	flexDirection: 'column',
	justifyContent: 'space-between',
	gap: '12px',
	marginBottom: '8px',
	minWidth: 0,
	overflow: 'hidden',
});

export const StyledNameAndStatusContainer = styled(Box)({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: '8px',
	minWidth: 0,
	'& > :first-of-type': {
		flex: '1 1 auto',
		minWidth: 0,
		overflow: 'hidden',
	},
	'& > :last-of-type': {
		flex: '0 0 auto',
		maxWidth: '200px',
	},
});

export const StyledTicketFooterContainer = styled(Stack, {
	shouldForwardProp: (propName) => propName !== 'isBlurred',
})<{ isBlurred?: boolean }>(({ isBlurred = false }) => ({
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: '16px',
	marginTop: '4px',
	...(isBlurred && { filter: 'blur(4px)' }),
}));

export const StyledTicketInfoSection = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	justifyContent: 'center',
	gap: '4px',
});

export const StyledQRCodeWrapper = styled(Box)({
	margin: 'auto',
	width: '100%',
	maxWidth: '500px',
});

export const TRUNCATE_TEXT_SX_PROPS: SxProps = {
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};
